
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { required, maxLength, requiredIf, integer } from '@vuelidate/validators';
import { adService } from '../AdService';
import { Message, MessageType } from '@/shared/models/Message';
import { environment } from '@/environment/environment';
import useVuelidate from '@vuelidate/core';
import { googleTagManagerService } from '@/shared/services/GoogleTagManagerService';
import decimal from '@/shared/helpers/decimalValidator';
import minValue from '@/shared/helpers/minValueValidator';
import { UploadFile } from '@/shared/models/UploadFile';
import FlashMessage from '@/shared/components/FlashMessage.vue';

export default defineComponent({
  mixins: [Form],
  emits: ['inserted'],
  components: {
    FlashMessage,
  },
  setup() {
    return { v$: useVuelidate({ $scope: false }) };
  },
  props: {
    ad: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tabActiveIndex: 0,
      importSampleFileUrl: null as null | string,
      statuses: [],
      estateSubTypes: [],
      floors: [],
      messages: [] as Message[],
      notificationClass: '',
      hasBalcony: false,
      hasLoggia: false,
      hasTerrace: false,
      importFile: null as null | UploadFile,
      form: {
        adId: 0,
        adFrontId: null as null | string,
        adGuid: '',
        estateFrontSubType: 103,
        title: '',
        floor: 1,
        price: null as null | number,
        hasEmptyPrice: false,
        estateArea: null as null | number,
        balconyArea: null as null | number,
        loggiaArea: null as null | number,
        terraceArea: null as null | number,
        status: 1,
      },
      initialData: {
        adId: 0,
        adFrontId: null as null | string,
        adGuid: '',
        estateFrontSubType: 103,
        title: '',
        floor: 1,
        price: null as null | number,
        hasEmptyPrice: false,
        estateArea: null as null | number,
        balconyArea: null as null | number,
        loggiaArea: null as null | number,
        terraceArea: null as null | number,
        status: 1,
      },
    };
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventNav);
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    const shouldAlert = this.isEditing;
    if (shouldAlert) {
      const answer = window.confirm(this.$t('page-leave-confirm'));
      if (!answer) {
        next(false);
        return;
      }
    }
    next();
  },
  validations() {
    const validations = {
      form: {
        estateFrontSubType: { required },
        title: { required, maxLength: maxLength(70) },
        floor: {
          required,
          integer,
        },
        price: {
          required: requiredIf(() => {
            return !this.form.hasEmptyPrice;
          }),
          integer,
          minValue: minValue(0),
        },
        hasEmptyPrice: {},
        estateArea: {
          required,
          decimal,
          minValue: (estateArea) => {
            let result = +estateArea.toString().replaceAll(',', '.') > 0;
            return result;
          },
        },
        balconyArea: {},
        loggiaArea: {},
        terraceArea: {},
        status: {},
      },
    };
    return validations;
  },
  async created() {
    await this.init();
  },

  computed: {
    showFormMessages(): boolean {
      return this.isSuccess || this.isError || this.errors.length > 0 || this.messages.length > 0;
    },
  },
  methods: {
    async init() {
      this.isLoading = true;
      try {
        const result = await adService.getNew();
        this.form.adGuid = result.data.model.adGuid;
        this.form.parentAdId = this.ad.adId;
        this.statuses = result.data.developerProjectStatuses;
        this.estateSubTypes = result.data.estateTypes.find((x) => x.key === 100).estateSubTypes;
        this.importSampleFileUrl = result.data.importSampleFileUrl;
        this.floors = [];
        for (let i = 1; i <= this.ad.maxFloor; i++) {
          this.floors.push({
            key: i,
            value: i + '/' + this.ad.maxFloor,
          });
        }
        this.isLoading = false;
        this.handleFlashMessages();
      } catch (error) {
        console.error(error);
      }
    },
    preventNav(event: any) {
      if (!this.isEditing || environment !== 'production') {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    },
    handleFlashMessages(appendMessages: Message[] = []) {
      const messages = [] as any[];
      if (appendMessages && appendMessages.length > 0) {
        messages.push(...appendMessages);
      }
      this.messages = messages;
      this.notificationClass = this.getNotificationClass();
    },
    getNotificationClass() {
      const errorExists = this.messages.some((x) => x.type === MessageType.Error);
      if (errorExists) return 'is-danger';
      const warningExists = this.messages.some((x) => x.type === MessageType.Warning);
      if (warningExists) return 'is-warning';
      return 'is-success';
    },
    beforeSave() {
      const form = Object.assign({}, this.form);
      return form;
    },
    async save() {
      this.isInserted = false;

      if (!this.validate()) {
        return;
      }
      const form = this.beforeSave();

      try {
        this.isLoading = true;
        await adService.insertDeveloperProjectEstate(form);
        this.isSuccess = true;
        this.logSuccess();
        this.handleFlashMessages();
        this.resetForm();
        this.inserted();
        await this.init();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.form = { ...this.initialData };
      this.v$.$reset();
    },
    logSuccess() {
      let event = 'pridane_developersky_projekt_nehnutelnost';

      googleTagManagerService.send({
        event: event,
      });
    },
    tabNavigate(index) {
      this.messages = [];
      this.errors = [];
      this.tabActiveIndex = index;
    },
    onEmptyPriceChange() {
      this.form.price = null;
    },
    inserted() {
      this.$emit('inserted');
    },
    async startImport() {
      try {
        this.messages = [];
        this.errors = [];
        this.isLoading = true;
        await adService.startImport({
          fileName: this.importFile.file,
          parentAdId: this.ad.adId,
        });
        this.messages.push({ type: MessageType.Success, text: this.$t('AdDeveloperProjectItem.import.success') });
        this.importFile = null;
        this.inserted();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async uploadFiles(file: string | Blob) {
      this.isUploading = true;
      this.importFile = {} as UploadFile;
      this.importFile.isUploading = true;
      this.importFile.uploadPercentage = 0;
      this.importFile.file = (file as any).name;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const result = await adService.uploadImport(formData, (progressEvent: any) => {
          this.importFile.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
        });
        this.importFile.file = result.data;
        this.importFile.isSuccess = true;
      } catch (error) {
        this.importFile.isError = true;
        this.importFile.text = 'Error';
        const errors = this.getApiErrors(error);
        this.importFile.text = errors[0].message;
      } finally {
        this.importFile.isUploading = false;
      }
      this.isUploading = false;
    },
    async handleFilesUpload() {
      let fileToUpload = (this.$refs.importFile as any).files as string[] | Blob[];
      if (fileToUpload.length === 0) {
        return;
      }
      await this.uploadFiles(fileToUpload[0]);
      (this.$refs.importFile as any).value = null;
    },
  },
});
